import React, { useEffect, useState } from 'react';
import { TutorialStep } from '../context/TutorialContext';

interface TutorialHighlightProps {
  step: TutorialStep;
  isActive: boolean;
}

export const TutorialHighlight: React.FC<TutorialHighlightProps> = ({ step, isActive }) => {
  const [highlightedElement, setHighlightedElement] = useState<Element | null>(null);

  useEffect(() => {
    if (!isActive || !step.highlightSelector) {
      setHighlightedElement(null);
      return;
    }

    // Find the element to highlight
    const element = document.querySelector(step.highlightSelector);
    if (element) {
      setHighlightedElement(element);

      // Add highlight class to the element
      element.classList.add('tutorial-highlight', `tutorial-highlight-${step.highlightType || 'glow'}`);

      return () => {
        element.classList.remove('tutorial-highlight', `tutorial-highlight-${step.highlightType || 'glow'}`);
      };
    }
  }, [step, isActive]);

  if (!isActive || !step.highlightSelector || !highlightedElement) {
    return null;
  }

  const rect = highlightedElement.getBoundingClientRect();

  return (
    <>
      {/* Spotlight effect - dark overlay with highlighted area */}
      {step.highlightType === 'spotlight' && (
        <div className="fixed inset-0 z-40 pointer-events-none">
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/60" />

          {/* Highlighted area cutout */}
          <div
            className="absolute border-4 border-yellow-400 rounded-lg animate-pulse"
            style={{
              top: rect.top - 8,
              left: rect.left - 8,
              width: rect.width + 16,
              height: rect.height + 16,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
              clipPath: `polygon(
                0% 0%,
                0% 100%,
                ${rect.left - 8}px 100%,
                ${rect.left - 8}px ${rect.top - 8}px,
                ${rect.left + rect.width + 8}px ${rect.top - 8}px,
                ${rect.left + rect.width + 8}px ${rect.top + rect.height + 8}px,
                ${rect.left - 8}px ${rect.top + rect.height + 8}px,
                ${rect.left - 8}px 100%,
                100% 100%,
                100% 0%
              )`
            }}
          />
        </div>
      )}

      {/* Pulse effect indicator */}
      {step.highlightType === 'pulse' && (
        <div
          className="fixed z-40 pointer-events-none border-4 border-yellow-400 rounded-lg animate-ping"
          style={{
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
          }}
        />
      )}

      {/* Glow effect - handled by CSS classes */}
      {/* Border effect - handled by CSS classes */}
    </>
  );
};

// Add CSS styles for highlight effects
const highlightStyles = `
  .tutorial-highlight {
    position: relative;
    z-index: 35;
    transition: all 0.3s ease;
  }

  .tutorial-highlight-glow {
    box-shadow: 0 0 20px rgba(251, 191, 36, 0.8), 0 0 40px rgba(251, 191, 36, 0.4);
    animation: tutorial-glow 2s ease-in-out infinite alternate;
  }

  .tutorial-highlight-border {
    border: 3px solid #fbbf24 !important;
    border-radius: 8px;
    animation: tutorial-border-pulse 1.5s ease-in-out infinite;
  }

  .tutorial-highlight-pulse {
    animation: tutorial-scale-pulse 1s ease-in-out infinite;
  }

  @keyframes tutorial-glow {
    from {
      box-shadow: 0 0 20px rgba(251, 191, 36, 0.8), 0 0 40px rgba(251, 191, 36, 0.4);
    }
    to {
      box-shadow: 0 0 30px rgba(251, 191, 36, 1), 0 0 60px rgba(251, 191, 36, 0.6);
    }
  }

  @keyframes tutorial-border-pulse {
    0%, 100% {
      border-color: #fbbf24;
    }
    50% {
      border-color: #f59e0b;
    }
  }

  @keyframes tutorial-scale-pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }
`;

// Inject styles into the document head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = highlightStyles;
  document.head.appendChild(styleSheet);
}
